/**
 * Viewer-side pre-checks on the bytes a shell is about to hand `open_ifc`.
 *
 * The engine is a parser, so it reports what a parser sees: a 0-byte file comes
 * back as `mesh buffer parse error: expected keyword 'ISO-10303-21' at token
 * index 0`, which is true and useless — the user's actual problem is an
 * interrupted download, a `.ifczip` they never extracted, or a file that was
 * never IFC. These checks name that problem *before* the engine is called.
 *
 * Two rules keep this from becoming a second, weaker parser:
 *
 * 1. **It only ever rejects on evidence in the first bytes.** Nothing here
 *    validates IFC. Anything that carries an `ISO-10303-21` header is handed
 *    straight to the engine, and the engine's own words are what the user sees
 *    when it then fails.
 * 2. **The one thing it repairs, it repairs visibly.** A UTF-8 BOM is stripped
 *    client-side (the engine's tokenizer will not skip it) and the strip is
 *    reported in the diagnostics panel, never silently.
 */

/** `EF BB BF`. Authoring tools that write UTF-8 with a BOM produce IFC the engine cannot open. */
const UTF8_BOM: readonly number[] = [0xef, 0xbb, 0xbf];

/** `PK\x03\x04`, a zip local file header — a `.ifczip` (or any zip) renamed to `.ifc`. */
const ZIP_MAGIC: readonly number[] = [0x50, 0x4b, 0x03, 0x04];

/**
 * How far in the SPF magic is looked for.
 *
 * The engine's own lexer (`crates/ifc-parse/src/spf.rs`) skips leading
 * whitespace and `/* *\/` / `--` comments with no distance limit at all, so
 * there is no byte count that is *guaranteed* to contain the keyword — only
 * one generous enough that no real exporter's preamble exceeds it. 4 KiB
 * comfortably covers a BOM, blank lines and even a multi-line license comment
 * before the header, while staying a cheap, bounded scan on files that can be
 * hundreds of MiB.
 */
const HEADER_SCAN_BYTES = 4096;

/** How much of the file's head is quoted back to the user on a rejection. */
export const HEAD_SAMPLE_CHARS = 40;

/** Read the leading bytes as ASCII, without pulling in a TextDecoder encoding label. */
function leadingAscii(bytes: Uint8Array, length: number): string {
  const end = Math.min(bytes.byteLength, length);
  let out = "";
  for (let index = 0; index < end; index += 1) {
    out += String.fromCharCode(bytes[index]);
  }
  return out;
}

/**
 * A missing bundled fixture can come back as a 200 carrying the shell's own HTML
 * (static-asset hosts and Tauri's asset protocol both fall back to the page).
 * Handing that to `open_ifc` would surface as a baffling IFC parse error, so the
 * SPF magic is checked first. Tolerates leading whitespace; a BOM is stripped by
 * {@link inspectInputBytes} before this is consulted.
 */
export function looksLikeIfcSpf(bytes: Uint8Array): boolean {
  return leadingAscii(bytes, HEADER_SCAN_BYTES).includes("ISO-10303-21");
}

/**
 * Quote the head of a file back to the user.
 *
 * Every byte outside printable ASCII becomes `·`, so a binary head cannot inject
 * control characters, newlines or bidi marks into the message. The result is
 * evidence, not content: it says "this is what your file starts with".
 */
export function sanitizeHead(bytes: Uint8Array, limit: number = HEAD_SAMPLE_CHARS): string {
  const end = Math.min(bytes.byteLength, limit);
  let out = "";
  for (let index = 0; index < end; index += 1) {
    const byte = bytes[index];
    out += byte >= 0x20 && byte <= 0x7e ? String.fromCharCode(byte) : "·";
  }
  return bytes.byteLength > limit ? `${out}…` : out;
}

function startsWith(bytes: Uint8Array, magic: readonly number[]): boolean {
  if (bytes.byteLength < magic.length) {
    return false;
  }
  return magic.every((value, index) => bytes[index] === value);
}

export type InputGuardReason = "empty" | "zip" | "not_spf";

export interface InputAccepted {
  readonly ok: true;
  /** The bytes to open — the input itself, or a BOM-stripped view of it. */
  readonly bytes: Uint8Array;
  /** Short diagnostics label, always set (`"ok"` when nothing was touched). */
  readonly note: string;
}

export interface InputRejected {
  readonly ok: false;
  readonly reason: InputGuardReason;
  /** Short diagnostics label. */
  readonly note: string;
  /** What the user is shown: one JA line, then the EN line. */
  readonly message: string;
  /**
   * Technical detail, shown below the message where the engine's own words go
   * on an engine failure. The guard never suppresses an engine error — it runs
   * before the engine is called at all.
   */
  readonly detail: string;
}

export type InputGuardResult = InputAccepted | InputRejected;

/**
 * Classify input bytes before the engine sees them.
 *
 * Order matters: emptiness first (every other check would misreport it), then
 * the zip magic (a zip's first bytes are not text and would produce a garbage
 * head sample), then the BOM repair, and only then the SPF header test.
 */
export function inspectInputBytes(bytes: Uint8Array): InputGuardResult {
  if (bytes.byteLength === 0) {
    return {
      ok: false,
      reason: "empty",
      note: "rejected — empty file (0 B)",
      message:
        "ファイルが空です（0 バイト）。ダウンロードが完了しているか確認してください。\n" +
        "The file is empty (0 bytes). Check that the download finished.",
      detail:
        "viewer-side pre-check: the file is 0 bytes long, so nothing was sent to open_ifc. " +
        "The engine was not called.",
    };
  }

  if (startsWith(bytes, ZIP_MAGIC)) {
    return {
      ok: false,
      reason: "zip",
      note: "rejected — zip archive (PK\\x03\\x04)",
      message:
        "これは ZIP 形式です（.ifczip の可能性）。展開してから開いてください。\n" +
        "This is a ZIP archive (possibly .ifczip). Extract it first and open the .ifc inside.",
      detail:
        "viewer-side pre-check: the file starts with 50 4b 03 04 (PK\\x03\\x04, a zip local file " +
        "header). This viewer reads IFC-SPF only; it does not unzip. The engine was not called.",
    };
  }

  // The BOM is stripped whether or not an SPF header follows: it is never valid
  // in an IFC-SPF file, and stripping first means the header test below sees
  // what the engine's tokenizer would see.
  const hadBom = startsWith(bytes, UTF8_BOM);
  const body = hadBom ? bytes.subarray(UTF8_BOM.length) : bytes;

  if (!looksLikeIfcSpf(body)) {
    const head = sanitizeHead(body);
    return {
      ok: false,
      reason: "not_spf",
      note: "rejected — no ISO-10303-21 header",
      message:
        `IFC-SPF のヘッダー（ISO-10303-21）が先頭にありません。ファイルは「${head}」で始まっています。` +
        "IFC ファイルかどうか確認してください（ifcXML・ifczip はこのビューアでは開けません）。\n" +
        `No IFC-SPF header (ISO-10303-21) at the start of the file. It begins with "${head}". ` +
        "Check that this really is an IFC (.ifc) file; ifcXML and ifczip are not supported here.",
      detail:
        `viewer-side pre-check: no "ISO-10303-21" in the first ${HEADER_SCAN_BYTES} bytes` +
        `${hadBom ? " (after stripping a UTF-8 BOM)" : ""}. The engine was not called; had it ` +
        "been, it would have reported a parse error at token index 0.",
    };
  }

  return {
    ok: true,
    bytes: body,
    note: hadBom ? `ok — UTF-8 BOM stripped (${UTF8_BOM.length} B)` : "ok",
  };
}
